import { EventBus } from './clients/event-bus';
import { EventStore } from './clients/event-store';
import { ManagementClient } from './clients/management';
/**
 * DeltaBase SDK main client
 */
import { HttpClient, type HttpClientConfig } from './utils/http';

/**
 * Configuration options for the DeltaBase client.
 */
export interface DeltaBaseConfig {
  /**
   * Base URL of the Delta-Base API.
   * For production use the default URL. For local development,
   * specify your local server address.
   * @default 'http://localhost:8787'
   */
  baseUrl?: string;

  /**
   * API key for authentication with the Delta-Base platform.
   * Required for all API operations. You can obtain this from
   * your Delta-Base dashboard.
   */
  apiKey?: string;

  /**
   * Custom headers to include with all requests.
   * Useful for internal service authentication or other custom headers.
   */
  headers?: Record<string, string>;

  /**
   * Custom fetch function to use for HTTP requests.
   * Useful for service bindings in Cloudflare Workers.
   * When provided, this will be used instead of the global fetch.
   */
  fetch?: typeof fetch;
}

/**
 * DeltaBase client for interacting with the Delta-Base platform.
 *
 * This is the main entry point for the SDK that provides access to:
 * - Management operations (create/delete event stores)
 * - Event store operations (append/read events)
 * - Event bus functionality (subscriptions and real-time updates)
 *
 * @example
 * ```typescript
 * // Initialize the client
 * const client = new DeltaBase({
 *   apiKey: "your-api-key"
 * });
 *
 * // Access various functionality
 * const management = client.getManagement();
 * const eventStore = client.getEventStore("my-store");
 * const eventBus = client.getEventBus("my-store");
 * ```
 */
export class DeltaBase {
  private http: HttpClient;

  /**
   * Creates a new DeltaBase client instance.
   *
   * @param config - Configuration options for connecting to the Delta-Base platform
   * @throws {Error} If neither apiKey nor headers are provided in the configuration
   *
   * @example
   * ```typescript
   * // Create with API key (standard authentication)
   * const client = new DeltaBase({ apiKey: "your-api-key" });
   *
   * // Create with custom URL (production)
   * const prodClient = new DeltaBase({
   *   baseUrl: "https://api.delta-base.com",
   *   apiKey: "your-api-key"
   * });
   *
   * // Create with custom headers (internal service authentication)
   * const internalClient = new DeltaBase({
   *   baseUrl: "https://api.delta-base.com",
   *   headers: {
   *     'X-Deltabase-Internal-Service': 'auth-service',
   *     'X-Deltabase-Internal-Token': 'internal-token'
   *   }
   * });
   * ```
   */
  constructor(config: DeltaBaseConfig) {
    if (!config.apiKey && !config.headers && !config.fetch) {
      throw new Error('Either apiKey, headers, or fetch must be provided');
    }

    const httpConfig: HttpClientConfig = {
      baseUrl: config.baseUrl || 'http://localhost:8787',
      apiKey: config.apiKey,
      headers: config.headers,
      fetch: config.fetch,
    };

    this.http = new HttpClient(httpConfig);
  }

  /**
   * Get a ManagementClient for managing event stores
   *
   * @returns A ManagementClient instance for creating and managing event stores
   * @example
   * ```typescript
   * const management = client.getManagement();
   * const eventStore = await management.createEventStore({
   *   name: "my-event-store"
   * });
   * ```
   */
  getManagement(): ManagementClient {
    return new ManagementClient(this.http);
  }

  /**
   * Get an EventStore client for a specific event store
   *
   * @param eventStoreName The name of the event store to connect to
   * @returns An EventStore client instance configured for the specified event store
   * @example
   * ```typescript
   * const eventStore = client.getEventStore("my-event-store");
   * await eventStore.appendToStream("user-123", [
   *   { type: "UserCreated", data: { userId: "123", name: "John" } }
   * ]);
   * ```
   */
  getEventStore(eventStoreName: string): EventStore {
    return new EventStore(this.http, eventStoreName);
  }

  /**
   * Get an EventBus client for a specific event store
   *
   * @param eventStoreName The name of the event store to manage subscriptions for
   * @returns An EventBus client instance configured for the specified event store
   * @example
   * ```typescript
   * const eventBus = client.getEventBus("my-event-store");
   * const subscription = await eventBus.createSubscription({
   *   name: "user-events",
   *   eventFilter: "user.*"
   * });
   * ```
   */
  getEventBus(eventStoreName: string): EventBus {
    return new EventBus(this.http, eventStoreName);
  }
}
